import { EnterForm } from '../components/EnterForm';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFY, isDateInFY, getFYDateError } from '../context/FYContext';
import { useLocation } from 'react-router-dom';
import { usePageCallbacks } from '../hooks/usePageCallbacks';
import { getRecurringEntries, createRecurringEntry, updateRecurringEntry, deleteRecurringEntry, bulkDeleteRecurring, approveRecurringEntry, postRecurringInstance, stopRecurringEntry, restartRecurringEntry, getAllUsersBasic, getEntryLedgers } from '../lib/api';
import { formatCurrency, getToday, formatDate, formatApiErrorDetail } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { SectionTiles } from '../components/SectionTiles';
import { Plus, RefreshCw, Check, X, AlertCircle, Pencil, Trash2, Play, StopCircle, RotateCcw, Clock, AlertTriangle, List } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { useSortableTable, SortableHeader } from '../components/ui/sortable-table';
import { DateInput } from '../components/ui/date-input';
import { SearchableSelect } from '../components/ui/searchable-select';
import { FormShortcutHints } from '../components/ui/form-shortcut-hints';
import { toast } from 'sonner';

const DEFAULT_INCOME_CATEGORIES = ['Sales', 'Investment', 'Transfer', 'Others'];
const DEFAULT_EXPENSE_CATEGORIES = ['Travel', 'Meals', 'Software', 'Marketing', 'Transfer', 'Others'];
const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' }
];
const TYPE_OPTIONS = [
  { value: 'income', label: 'Income' },
  { value: 'expense', label: 'Expense' }
];

const Recurring = () => {
  const { isAdmin } = useAuth();
  const { selectedFY, fyStartDate, fyEndDate, shortLabel: fyLabel } = useFY();
  const location = useLocation();
  const [entries, setEntries] = useState([]);
  const [incomeCategories, setIncomeCategories] = useState(DEFAULT_INCOME_CATEGORIES);
  const [expenseCategories, setExpenseCategories] = useState(DEFAULT_EXPENSE_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [postingEntry, setPostingEntry] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [postDate, setPostDate] = useState(getToday());
  const [selectedIds, setSelectedIds] = useState([]);
  const [users, setUsers] = useState([]);
  const [entryLedgers, setEntryLedgers] = useState([]);

  const [formData, setFormData] = useState({
    entry_type: 'expense',
    amount: '',
    category: '',
    remarks: '',
    frequency: 'monthly',
    next_date: getToday(),
    end_date: '',
    user_id: '',
    entry_ledger_id: ''
  });

  // Add stop dialog state
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [stoppingEntry, setStoppingEntry] = useState(null);
  const [stopReason, setStopReason] = useState('');

  // Determine default tab from navigation state
  const defaultTab = location.state?.tab || '';
  const [activeTab, setActiveTab] = useState(defaultTab);

  const [dataLoaded, setDataLoaded] = useState(false);
  useEffect(() => { if (activeTab && !dataLoaded) { fetchData(); setDataLoaded(true); } }, [activeTab]); // eslint-disable-line

  useEffect(() => {
    getAllUsersBasic().then(res => setUsers(res.data)).catch(() => {});
    getEntryLedgers().then(res => setEntryLedgers(res.data)).catch(() => {});
  }, []);

  const fetchData = async () => {
    setSelectedIds([]);
    try {
      const res = await getRecurringEntries();
      setEntries(res.data);
    } catch (err) { toast.error('Failed to fetch data'); } finally { setLoading(false); }
  };

  const resetForm = () => {
    setFormData({ entry_type: 'expense', amount: '', category: '', remarks: '', frequency: 'monthly', next_date: getToday(), end_date: '', user_id: '', entry_ledger_id: '' });
    setEditingEntry(null); setError('');
  };

  const openCreateDialog = () => { resetForm(); setDialogOpen(true); };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setFormData({
      entry_type: entry.entry_type,
      amount: entry.amount.toString(),
      category: entry.category,
      remarks: entry.remarks || '',
      frequency: entry.frequency,
      next_date: entry.next_date,
      end_date: entry.end_date || '',
      user_id: entry.user_id || '',
      entry_ledger_id: entry.entry_ledger_id || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSubmitting(true);
    if (!isDateInFY(formData.next_date, selectedFY)) { setError(getFYDateError(selectedFY, fyLabel)); setSubmitting(false); return; }
    const payload = {
      entry_type: formData.entry_type,
      amount: parseFloat(formData.amount),
      category: formData.category,
      remarks: formData.remarks,
      frequency: formData.frequency,
      next_date: formData.next_date,
      end_date: formData.end_date || null,
      user_id: isAdmin ? formData.user_id : undefined,
      entry_ledger_id: formData.entry_ledger_id || undefined
    };
    try {
      if (editingEntry) {
        await updateRecurringEntry(editingEntry.id, payload);
        toast.success('Recurring entry updated');
      } else {
        await createRecurringEntry(payload);
        toast.success('Recurring entry created. Awaiting admin approval.');
      }
      setDialogOpen(false); resetForm(); fetchData();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSubmitting(false); }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete this recurring ${entry.entry_type} entry?`)) return;
    try {
      await deleteRecurringEntry(entry.id);
      toast.success('Recurring entry deleted');
      fetchData();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const handleApproval = async (entryId, approve) => {
    try {
      await approveRecurringEntry(entryId, approve);
      toast.success(approve ? 'Entry approved' : 'Entry rejected');
      fetchData();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const [postRemarks, setPostRemarks] = useState('');

  const openPostDialog = (entry) => {
    setPostingEntry(entry);
    setPostDate(entry.next_date || getToday());
    setPostRemarks('');
    setError('');
    setPostDialogOpen(true);
  };

  const handlePostInstance = async (e) => {
    e.preventDefault(); setError(''); setSubmitting(true);
    if (!isDateInFY(postDate, selectedFY)) { setError(getFYDateError(selectedFY, fyLabel)); setSubmitting(false); return; }
    try {
      await postRecurringInstance(postingEntry.id, postDate, postRemarks);
      toast.success(`${postingEntry.entry_type} entry posted for ${formatDate(postDate)}`);
      setPostDialogOpen(false); setPostingEntry(null); fetchData();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSubmitting(false); }
  };

  const openStopDialog = (entry) => {
    setStoppingEntry(entry);
    setStopReason('');
    setStopDialogOpen(true);
  };

  const handleStop = async (e) => {
    e.preventDefault(); setError(''); setSubmitting(true);
    try {
      await stopRecurringEntry(stoppingEntry.id, stopReason);
      toast.success('Recurring entry stopped');
      setStopDialogOpen(false); setStoppingEntry(null); fetchData();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSubmitting(false); }
  };

  const handleRestart = async (entryId) => {
    try {
      await restartRecurringEntry(entryId);
      toast.success('Recurring entry restarted');
      fetchData();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const handleCreateCategory = (newCategory) => {
    if (formData.entry_type === 'income') {
      setIncomeCategories(prev => [...prev, newCategory]);
    } else {
      setExpenseCategories(prev => [...prev, newCategory]);
    }
    setFormData(prev => ({ ...prev, category: newCategory }));
  };

  const getStatusBadge = (status) => {
    const variants = { pending_approval: 'warning', approved: 'default', rejected: 'destructive', stopped: 'destructive' };
    const labels = { pending_approval: 'Pending Approval', approved: 'Active', rejected: 'Rejected', stopped: 'Stopped' };
    return <Badge variant={variants[status]}>{labels[status] || status}</Badge>;
  };

  // Selection logic (admin only for recurring)
  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected recurring entries?`)) return;
    try {
      const res = await bulkDeleteRecurring(selectedIds);
      toast.success(res.data.message);
      setSelectedIds([]);
      fetchData();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const categories = formData.entry_type === 'income' ? incomeCategories : expenseCategories;
  const categoryOptions = categories.map(cat => ({ value: cat, label: cat }));

  // Separate entries by status
  const pendingEntries = entries.filter(e => e.status === 'pending_approval');
  const approvedEntries = entries.filter(e => e.status === 'approved');
  const stoppedEntries = entries.filter(e => e.status === 'stopped');
  const dueEntries = approvedEntries.filter(e => e.next_date <= getToday());
  const { sortedData: sortedApproved, sortKey: recSortKey, sortDirection: recSortDir, handleSort: handleRecSort } = useSortableTable(approvedEntries, 'next_date', 'asc');

  // Listen for quick-open tab switch events
  useEffect(() => {
    const handleQuickOpenTab = (e) => {
      const tab = e.detail?.tab;
      if (tab && ['due', 'pending', 'stopped', 'all'].includes(tab)) setActiveTab(tab);
    };
    window.addEventListener('quickopen-tab', handleQuickOpenTab);
    return () => window.removeEventListener('quickopen-tab', handleQuickOpenTab);
  }, []);

  const REC_TABS = ['due', ...(isAdmin ? ['pending'] : []), 'stopped', 'all'];
  usePageCallbacks({
    onNewEntry: () => { resetForm(); setDialogOpen(true); },
    onPrevTab: () => setActiveTab(t => { const i = REC_TABS.indexOf(t); if (i < 0) return REC_TABS[0]; return REC_TABS[(i - 1 + REC_TABS.length) % REC_TABS.length]; }),
    onNextTab: () => setActiveTab(t => { const i = REC_TABS.indexOf(t); if (i < 0) return REC_TABS[0]; return REC_TABS[(i + 1) % REC_TABS.length]; }),
  });

  if (loading && activeTab) {
    return <div className="space-y-6"><Skeleton className="h-8 w-32" /><Skeleton className="h-64 w-full" /></div>;
  }

  const EntryTable = ({ data, showActions, showApproval, showPost }) => {
    const allSelected = isAdmin && data.length > 0 && data.every(e => selectedIds.includes(e.id));
    const toggleAll = () => {
      if (allSelected) {
        setSelectedIds(prev => prev.filter(id => !data.some(e => e.id === id)));
      } else {
        setSelectedIds(prev => [...new Set([...prev, ...data.map(e => e.id)])]);
      }
    };

    const [expandedIds, setExpandedIds] = useState([]);
    const toggleExpand = (id) => setExpandedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    return (
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdmin && <TableHead className="w-10 cursor-pointer hidden sm:table-cell" onClick={toggleAll}>
                    <Checkbox checked={allSelected} className="pointer-events-none" data-testid="select-all-recurring" aria-label="Select all" />
                  </TableHead>}
                  <TableHead>Type</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="hidden sm:table-cell">Frequency</TableHead>
                  <TableHead className="hidden sm:table-cell">Ledger</TableHead>
                  <TableHead className="hidden sm:table-cell">User</TableHead>
                  <TableHead>Next Date</TableHead>
                  <TableHead className="hidden sm:table-cell">End Date</TableHead>
                  <TableHead className="hidden sm:table-cell">Status</TableHead>
                  <TableHead className="hidden sm:table-cell">Last Posted</TableHead>
                  <TableHead className="text-right hidden sm:table-cell">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.length === 0 ? (
                  <TableRow><TableCell colSpan={12} className="text-center py-8 text-muted-foreground">No entries</TableCell></TableRow>
                ) : data.map(entry => {
                  const isDue = entry.status === 'approved' && entry.next_date <= getToday();
                  const isExpanded = expandedIds.includes(entry.id);
                  return (
                    <React.Fragment key={entry.id}>
                    <TableRow data-testid={`recurring-row-${entry.id}`} className={`${isDue ? 'bg-destructive/5' : ''} ${selectedIds.includes(entry.id) ? 'bg-muted/50' : ''} sm:cursor-default cursor-pointer`} onClick={() => toggleExpand(entry.id)}>
                      {isAdmin && <TableCell className="cursor-pointer hidden sm:table-cell" onClick={(e) => { e.stopPropagation(); toggleSelect(entry.id); }}>
                        <Checkbox checked={selectedIds.includes(entry.id)} className="pointer-events-none" data-testid={`select-recurring-${entry.id}`} />
                      </TableCell>}
                      <TableCell>
                        <span className="sm:hidden mr-1 text-muted-foreground">{isExpanded ? '▾' : '▸'}</span>
                        <Badge variant={entry.entry_type === 'income' ? 'default' : 'secondary'} className="text-xs">{entry.entry_type}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{entry.category}</TableCell>
                      <TableCell className={`text-right font-mono text-sm ${entry.entry_type === 'income' ? 'text-income' : 'text-expense'}`}>{formatCurrency(entry.amount)}</TableCell>
                      <TableCell className="hidden sm:table-cell capitalize">{entry.frequency}</TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{entryLedgers.find(l => l.id === entry.entry_ledger_id)?.name || '-'}</TableCell>
                      <TableCell className="hidden sm:table-cell text-sm">{entry.user_name || entry.created_by_name || '-'}</TableCell>
                      <TableCell className="text-sm">
                        {formatDate(entry.next_date)}
                        {isDue && <Badge variant="destructive" className="ml-1 text-[10px] px-1">DUE</Badge>}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{entry.end_date ? formatDate(entry.end_date) : '-'}</TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {getStatusBadge(entry.status)}
                        {entry.status === 'stopped' && entry.stop_reason && (
                          <p className="text-[10px] text-muted-foreground mt-0.5" title={entry.stop_reason}>Reason: {entry.stop_reason.substring(0, 30)}</p>
                        )}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{entry.last_posted ? formatDate(entry.last_posted) : '-'}</TableCell>
                      <TableCell className="text-right hidden sm:table-cell">
                        <div className="flex justify-end gap-1">
                          {showApproval && entry.status === 'pending_approval' && isAdmin && (
                            <>
                              <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 text-income hover:text-income" onClick={(e) => { e.stopPropagation(); handleApproval(entry.id, true); }} data-testid={`approve-recurring-${entry.id}`} title="Approve"><Check className="h-3.5 w-3.5" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 text-expense hover:text-expense" onClick={(e) => { e.stopPropagation(); handleApproval(entry.id, false); }} data-testid={`reject-recurring-${entry.id}`} title="Reject"><X className="h-3.5 w-3.5" /></Button>
                            </>
                          )}
                          {showPost && entry.status === 'approved' && (
                            <>
                              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); openPostDialog(entry); }} data-testid={`post-recurring-${entry.id}`}>
                                <Play className="h-3 w-3 mr-1" />Post
                              </Button>
                              <Button variant="outline" size="sm" className="h-7 text-xs text-destructive" onClick={(e) => { e.stopPropagation(); openStopDialog(entry); }} data-testid={`stop-recurring-${entry.id}`}>
                                <StopCircle className="h-3 w-3 mr-1" />Stop
                              </Button>
                            </>
                          )}
                          {entry.status === 'stopped' && isAdmin && (
                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); handleRestart(entry.id); }} data-testid={`restart-recurring-${entry.id}`}>
                              <RotateCcw className="h-3 w-3 mr-1" />Restart
                            </Button>
                          )}
                          {showActions && isAdmin && (
                            <>
                              <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8" onClick={(e) => { e.stopPropagation(); openEditDialog(entry); }} data-testid={`edit-recurring-${entry.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 text-destructive hover:text-destructive" onClick={(e) => { e.stopPropagation(); handleDelete(entry); }} data-testid={`delete-recurring-${entry.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow className="sm:hidden bg-muted/20">
                        <TableCell colSpan={4} className="py-2 px-4">
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                            <span className="text-muted-foreground">Frequency</span>
                            <span className="text-right capitalize">{entry.frequency}</span>
                            <span className="text-muted-foreground">User</span>
                            <span className="text-right">{entry.user_name || entry.created_by_name || '-'}</span>
                            <span className="text-muted-foreground">Ledger</span>
                            <span className="text-right">{entryLedgers.find(l => l.id === entry.entry_ledger_id)?.name || '-'}</span>
                            <span className="text-muted-foreground">End Date</span>
                            <span className="text-right">{entry.end_date ? formatDate(entry.end_date) : '-'}</span>
                            <span className="text-muted-foreground">Status</span>
                            <span className="text-right capitalize">{entry.status.replace('_', ' ')}</span>
                            <span className="text-muted-foreground">Last Posted</span>
                            <span className="text-right">{entry.last_posted ? formatDate(entry.last_posted) : '-'}</span>
                            {entry.status === 'stopped' && entry.stop_reason && <>
                              <span className="text-muted-foreground">Stop Reason</span>
                              <span className="text-right">{entry.stop_reason}</span>
                            </>}
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-border/40">
                            {showApproval && entry.status === 'pending_approval' && isAdmin && (
                              <>
                                <Button variant="default" size="sm" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); handleApproval(entry.id, true); }}>
                                  <Check className="h-3 w-3 mr-1" />Approve
                                </Button>
                                <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); handleApproval(entry.id, false); }}>
                                  <X className="h-3 w-3 mr-1" />Reject
                                </Button>
                              </>
                            )}
                            {showPost && entry.status === 'approved' && (
                              <>
                                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); openPostDialog(entry); }}>
                                  <Play className="h-3 w-3 mr-1" />Post
                                </Button>
                                <Button variant="outline" size="sm" className="h-7 text-xs text-destructive" onClick={(e) => { e.stopPropagation(); openStopDialog(entry); }}>
                                  <StopCircle className="h-3 w-3 mr-1" />Stop
                                </Button>
                              </>
                            )}
                            {entry.status === 'stopped' && isAdmin && (
                              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); handleRestart(entry.id); }}>
                                <RotateCcw className="h-3 w-3 mr-1" />Restart
                              </Button>
                            )}
                            {showActions && isAdmin && (
                              <>
                                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); openEditDialog(entry); }}>
                                  <Pencil className="h-3 w-3 mr-1" />Edit
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive hover:text-destructive" onClick={(e) => { e.stopPropagation(); handleDelete(entry); }}>
                                  <Trash2 className="h-3 w-3 mr-1" />Delete
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    );
  };

  const SECTION_DEFS = [
    { key: '1', value: 'due', label: `Due${dueEntries.length ? ` (${dueEntries.length})` : ''}`, icon: Clock },
    ...(isAdmin ? [{ key: '2', value: 'pending', label: `Pending${pendingEntries.length ? ` (${pendingEntries.length})` : ''}`, icon: AlertTriangle }] : []),
    { key: isAdmin ? '3' : '2', value: 'stopped', label: `Stopped${stoppedEntries.length ? ` (${stoppedEntries.length})` : ''}`, icon: StopCircle },
    { key: isAdmin ? '4' : '3', value: 'all', label: 'All', icon: List },
  ];

  return (
    <SectionTiles
      sections={SECTION_DEFS}
      activeSection={activeTab}
      onSelect={(v) => { setActiveTab(v); setSelectedIds([]); }}
      pageTitle="Recurring"
      pageDescription="Manage recurring entries and schedules"
      headerActions={<Button size="sm" onClick={() => openCreateDialog()} data-testid="add-recurring-btn"><Plus className="h-4 w-4 mr-1" />Add Recurring</Button>}
    >

    <div className="space-y-4">
      {/* Due entries alert */}
      {dueEntries.length > 0 && activeTab === 'due' && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{dueEntries.length} recurring {dueEntries.length === 1 ? 'entry is' : 'entries are'} due for posting.</AlertDescription>
        </Alert>
      )}

      {activeTab === 'due' && (
        <div className="space-y-4">
          {dueEntries.length > 0 ? <EntryTable data={dueEntries} showActions={true} showApproval={false} showPost={true} /> : <div className="text-center py-8 text-muted-foreground">No due entries</div>}
        </div>
      )}

      {isAdmin && activeTab === 'pending' && (
        <div className="space-y-4">
          {pendingEntries.length > 0 ? <EntryTable data={pendingEntries} showActions={true} showApproval={true} showPost={false} /> : <div className="text-center py-8 text-muted-foreground">No pending entries</div>}
        </div>
      )}

      {activeTab === 'stopped' && (
        <div className="space-y-4">
          {stoppedEntries.length > 0 ? <EntryTable data={stoppedEntries} showActions={true} showApproval={false} showPost={false} /> : <div className="text-center py-8 text-muted-foreground">No stopped entries</div>}
        </div>
      )}

      {activeTab === 'all' && (
        <div className="space-y-4">
          <EntryTable data={entries} showActions={true} showApproval={true} showPost={true} />
        </div>
      )}

      {/* Bulk Action Bar */}
      {isAdmin && selectedIds.length > 0 && (
        <div className="fixed bottom-14 sm:bottom-14 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 px-5 py-3 rounded-full shadow-lg border bg-background" data-testid="recurring-bulk-action-bar">
          <span className="text-sm font-medium">{selectedIds.length} selected</span>
          <Button variant="outline" size="sm" onClick={() => setSelectedIds([])} data-testid="clear-recurring-selection-btn">Clear</Button>
          <Button variant="destructive" size="sm" onClick={handleBulkDelete} data-testid="bulk-delete-recurring-btn">
            <Trash2 className="h-4 w-4 mr-1" />Delete selected
          </Button>
        </div>
      )}

      {/* Post Instance Dialog */}
      <Dialog open={postDialogOpen} onOpenChange={setPostDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Post Recurring Entry</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handlePostInstance} className="space-y-4">
            {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
            {postingEntry && (
              <div className="p-3 rounded-md bg-muted space-y-1">
                <p className="text-sm"><span className="text-muted-foreground">Type:</span> <Badge variant={postingEntry.entry_type === 'income' ? 'default' : 'secondary'}>{postingEntry.entry_type}</Badge></p>
                <p className="text-sm"><span className="text-muted-foreground">Category:</span> {postingEntry.category}</p>
                <p className="text-sm"><span className="text-muted-foreground">Amount:</span> <span className="font-mono font-medium">{formatCurrency(postingEntry.amount)}</span></p>
                <p className="text-sm"><span className="text-muted-foreground">Frequency:</span> {postingEntry.frequency}</p>
                <p className="text-sm"><span className="text-muted-foreground">User:</span> {postingEntry.user_name || '-'}</p>
                <p className="text-sm"><span className="text-muted-foreground">Created by:</span> {postingEntry.created_by_name}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Post Date</Label>
              <DateInput fyStartYear={selectedFY} value={postDate} onChange={(e) => { const v = e.target.value; setPostDate(v); if (v && !isDateInFY(v, selectedFY)) toast.error(getFYDateError(selectedFY, fyLabel)); }} required data-testid="post-instance-date" />
              <p className="text-xs text-muted-foreground">This will create an actual {postingEntry?.entry_type} entry and advance the next date.</p>
            </div>
            <div className="space-y-2">
              <Label>Remarks (Optional)</Label>
              <Textarea placeholder="Add remarks for this posted entry..." value={postRemarks} onChange={(e) => setPostRemarks(e.target.value)} data-testid="post-instance-remarks" />
            </div>
            <DialogFooter>
              <FormShortcutHints />
              <Button type="button" variant="outline" onClick={() => setPostDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="post-instance-submit">{submitting ? 'Posting...' : 'Post Entry'}</Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>

      {/* Stop Dialog */}
      <Dialog open={stopDialogOpen} onOpenChange={setStopDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Stop Recurring Entry</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleStop} className="space-y-4">
            {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
            {stoppingEntry && (
              <div className="p-3 rounded-md bg-muted space-y-1">
                <p className="text-sm"><span className="text-muted-foreground">Entry:</span> {stoppingEntry.category} - {formatCurrency(stoppingEntry.amount)}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Reason for Stopping</Label>
              <Textarea placeholder="Please provide a reason..." value={stopReason} onChange={(e) => setStopReason(e.target.value)} required data-testid="stop-reason-input" />
            </div>
            <DialogFooter>
              <FormShortcutHints />
              <Button type="button" variant="outline" onClick={() => setStopDialogOpen(false)}>Cancel</Button>
              <Button type="submit" variant="destructive" disabled={submitting || !stopReason.trim()} data-testid="stop-confirm-btn">{submitting ? 'Stopping...' : 'Stop Entry'}</Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>
    </div>
    </SectionTiles>
  );
};

export default Recurring;
